import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Box, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Typography } from '@mui/material';
import { getMemberMemos, addMemberMemo, updateMemberMemoById, deleteMemberMemo } from '../../../api/user';
import { getBranchManager } from '../../../api/staff';
import { MEMBER_ADDITION_INFO_KEY } from '../../../constants/queryKeys';
import CommonModalLayout from '../../../form-components/CommonModalLayout';
import CommonModalButton from '../../../form-components/CommonModalButton';
import CommonSelect from '../../../form-components/CommonSelect';
import {
  MemoModalTextarea,
  MemoLayoutRow,
  MemoInputSection,
  MemoList,
  MemoListHeader,
  MemoItem,
  MemoItemActions,
  MemoActionButton,
  MemoContent,
  MemoItemMeta,
} from './UserDetailStyled';

export const MEMO_MAX_LENGTH = 2000;

const formatDate = dateStr => {
  if (!dateStr) return '';
  return dateStr.replace(/-/g, '.').slice(0, 16);
};

const MemoEditModal = ({ open, onClose, memberId, branchId, token }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [newMemo, setNewMemo] = useState('');
  const [newManagerId, setNewManagerId] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');

  useEffect(() => {
    if (open) {
      setNewMemo('');
      setNewManagerId('');
      setEditingId(null);
      setEditingText('');
      setDeleteTarget(null);
    }
  }, [open]);

  const invalidateMemos = () => {
    queryClient.invalidateQueries({ queryKey: ['memberMemos', memberId, branchId] });
    queryClient.invalidateQueries({ queryKey: MEMBER_ADDITION_INFO_KEY(memberId, branchId) });
  };

  // 담당자는 강사만이 아니라 이 지점 재직중 임직원 전체다.
  const { data: managers = [] } = useQuery({
    queryKey: ['branchManagers', branchId],
    queryFn: () => getBranchManager(branchId, token),
    enabled: open && !!branchId && !!token,
  });

  // 메모의 managerId 는 Employee ID 공간이라 managerId(User ID)가 아니라 employeeId 를 보낸다.
  const managerOptions = managers.map(manager => ({
    value: manager.employeeId,
    label: manager.managerName,
  }));

  const { data: memos = [] } = useQuery({
    queryKey: ['memberMemos', memberId, branchId],
    queryFn: () => getMemberMemos(memberId, branchId, token),
    enabled: open && !!memberId && !!branchId && !!token,
  });

  const addMutation = useMutation({
    mutationFn: () => addMemberMemo(memberId, { branchId, memo: newMemo, managerId: newManagerId || null }, token),
    onSuccess: () => {
      toast.success(t('메모가 추가되었습니다'));
      setNewMemo('');
      setNewManagerId('');
      invalidateMemos();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ memoId, memo }) => updateMemberMemoById(memberId, memoId, { branchId, memo }, token),
    onSuccess: () => {
      toast.success(t('메모가 수정되었습니다'));
      setEditingId(null);
      setEditingText('');
      invalidateMemos();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: memoId => deleteMemberMemo(memberId, memoId, token),
    onSuccess: () => {
      toast.success(t('메모가 삭제되었습니다'));
      setDeleteTarget(null);
      invalidateMemos();
    },
  });

  const handleAdd = () => {
    if (!newMemo.trim()) {
      toast.error(t('메모를 입력해 주세요.'));
      return;
    }
    addMutation.mutate();
  };

  const handleEditStart = memo => {
    setEditingId(memo.id);
    setEditingText(memo.memo);
  };

  const handleEditSave = () => {
    if (!editingText.trim()) {
      toast.error(t('메모를 입력해 주세요.'));
      return;
    }
    updateMutation.mutate({ memoId: editingId, memo: editingText.trim() });
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget);
  };

  return (
    <>
      <CommonModalLayout
        open={open}
        onClose={onClose}
        title={t('메모')}
        width={920}
        footer={
          <CommonModalButton variant="cancel" onClick={onClose}>
            {t('닫기')}
          </CommonModalButton>
        }>
        <MemoLayoutRow>
          <MemoInputSection>
            <MemoModalTextarea
              value={newMemo}
              maxLength={MEMO_MAX_LENGTH}
              placeholder={t('새 메모를 입력해 주세요.')}
              onChange={e => setNewMemo(e.target.value)}
              style={{ flex: 1, resize: 'none' }}
            />
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <CommonSelect
                value={newManagerId}
                onChange={e => setNewManagerId(e.target.value)}
                options={managerOptions}
                emptyLabel="담당자 선택"
                width={200}
              />
              <CommonModalButton variant="submit" onClick={handleAdd} disabled={addMutation.isPending} sx={{ ml: 'auto', height: '36px', fontSize: '13px', minWidth: '56px' }}>
                {t('추가')}
              </CommonModalButton>
            </Box>
          </MemoInputSection>

          <MemoList>
            <MemoListHeader>{t('메모 목록')} ({memos.length})</MemoListHeader>
            {memos.length === 0 && (
              <MemoItemMeta>{t('등록된 메모가 없습니다.')}</MemoItemMeta>
            )}
            {memos.map(memo => (
              <MemoItem key={memo.id}>
                {editingId === memo.id ? (
                  <>
                    <MemoModalTextarea
                      minRows={3}
                      maxRows={8}
                      value={editingText}
                      maxLength={MEMO_MAX_LENGTH}
                      onChange={e => setEditingText(e.target.value)}
                      style={{ width: '100%', fontSize: '13px' }}
                    />
                    <MemoItemActions>
                      <MemoActionButton variant="save" onClick={handleEditSave} disabled={updateMutation.isPending}>
                        저장
                      </MemoActionButton>
                      <MemoActionButton variant="cancel" onClick={() => { setEditingId(null); setEditingText(''); }}>
                        취소
                      </MemoActionButton>
                    </MemoItemActions>
                  </>
                ) : (
                  <>
                    <MemoContent>{memo.memo}</MemoContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: '10px', pt: '8px', borderTop: '1px solid #eee' }}>
                      <MemoItemActions sx={{ m: 0, p: 0, border: 'none' }}>
                        <MemoActionButton variant="edit" onClick={() => handleEditStart(memo)}>
                          수정
                        </MemoActionButton>
                        <MemoActionButton variant="delete" onClick={() => setDeleteTarget(memo.id)}>
                          삭제
                        </MemoActionButton>
                      </MemoItemActions>
                      <MemoItemMeta sx={{ m: 0 }}>
                        <span>담당자: {memo.createdByName || '-'}</span>
                        <span style={{ margin: '0 6px' }}>/</span>
                        <span>등록일: {formatDate(memo.createdAt)}</span>
                      </MemoItemMeta>
                    </Box>
                  </>
                )}
              </MemoItem>
            ))}
          </MemoList>
        </MemoLayoutRow>
      </CommonModalLayout>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>{t('메모 삭제')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px', color: '#333' }}>{t('이 메모를 삭제하시겠습니까?')}</Typography>
          <Typography sx={{ fontSize: '13px', color: '#999', mt: '6px' }}>{t('삭제된 메모는 복구할 수 없습니다.')}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: '12px 24px' }}>
          <CommonModalButton variant="cancel" onClick={() => setDeleteTarget(null)}>{t('취소')}</CommonModalButton>
          <CommonModalButton variant="delete" onClick={handleConfirmDelete} disabled={deleteMutation.isPending}>
            {t('삭제')}
          </CommonModalButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MemoEditModal;
